/**
 * t337u: the checklist template form (desktop TemplateEditor in apps/web features/ops/ChecklistsPage), kept pure so the
 * desktop, the phone web page and the native app build the same template from the same fields.
 */
import type { ChecklistCategory, ChecklistItem, ChecklistTemplate } from './checklists';
import type { ChecklistKind } from './registers';
import { sameJson } from '../forms';
import { cleanListSections, type ListSection } from '../formSections';

export type TemplateItemDraft = Omit<ChecklistItem, 'id'> & { id?: string };
export interface TemplateForm {
  kind: ChecklistKind; name: string; description: string; category: ChecklistCategory; is_audit: boolean; pass_mark: number; task_project_id: string;
  items: TemplateItemDraft[];
  register_location_id: string; register_team_ids: string[]; register_position_ids: string[];
  /** t695u: the buildings a checklist is used at ([] = all buildings). */
  location_ids: string[];
  /** t721u: the template's icon ('' = the default). Optional so older forms that don't set it leave it as it is. */
  emoji?: string;
  /**
   * t725u: section headings between items (`at` counts the items above). Only the desktop builder sets them; forms
   * that leave them out (phone and app editors) don't send them, and the server keeps the headings beside their items.
   */
  sections?: ListSection[];
}
/** Who can create and edit checklist templates (desktop Settings → Operations → Checklist templates). */
export const TEMPLATE_MANAGE_PERM = 'ops.checklist.template.manage';
export const TEMPLATE_KINDS: readonly { id: ChecklistKind; name: string }[] = [{ id: 'checklist', name: 'Checklist' }, { id: 'register', name: 'Attendance register' }];
export const ITEM_ON_FAIL_OPTIONS: readonly { id: NonNullable<ChecklistItem['on_fail']>; name: string }[] = [
  { id: 'none', name: 'Just record it' }, { id: 'task', name: 'Create a task' }, { id: 'incident', name: 'Raise an incident' },
];

export const blankTemplateItem = (): TemplateItemDraft => ({ type: 'tick', label: '', required: true, weight: 1, on_fail: 'none' });

export function initialTemplateForm(tpl: ChecklistTemplate | null): TemplateForm {
  return {
    kind: tpl?.kind ?? 'checklist', name: tpl?.name ?? '', description: tpl?.description ?? '', category: tpl?.category ?? 'other',
    is_audit: tpl?.is_audit ?? false, pass_mark: tpl?.pass_mark ?? 80, task_project_id: tpl?.task_project_id ?? '',
    items: tpl?.items.length ? tpl.items.map(i => ({ ...i })) : [blankTemplateItem()],
    register_location_id: tpl?.register_location_id ?? '', register_team_ids: tpl?.register_team_ids ?? [], register_position_ids: tpl?.register_position_ids ?? [],
    location_ids: tpl?.location_ids ?? [],
    emoji: tpl?.emoji ?? '',
  };
}

/** Which of an item's extra settings apply (as on desktop). */
export function itemOptions(it: Pick<TemplateItemDraft, 'type'>, isAudit: boolean) {
  return {
    range: it.type === 'number',
    passAnswer: it.type === 'yes_no_na',
    points: isAudit && (it.type === 'tick' || it.type === 'yes_no_na' || it.type === 'number'),
    onFail: it.type === 'yes_no_na' || it.type === 'number',
  };
}

export function setTemplateItem(f: TemplateForm, i: number, patch: Partial<TemplateItemDraft>): TemplateForm {
  return { ...f, items: f.items.map((x, j) => j === i ? { ...x, ...patch } : x) };
}
export function moveTemplateItem(f: TemplateForm, i: number, d: -1 | 1): TemplateForm {
  const j = i + d;
  if (j < 0 || j >= f.items.length) return f;
  const items = [...f.items];
  const [x] = items.splice(i, 1);
  items.splice(j, 0, x!);
  return { ...f, items };
}
export const removeTemplateItem = (f: TemplateForm, i: number): TemplateForm => ({ ...f, items: f.items.filter((_, j) => j !== i) });
export const addTemplateItem = (f: TemplateForm): TemplateForm => ({ ...f, items: [...f.items, blankTemplateItem()] });
/** t721u: add an item at a position (the builder adds after the selected item). */
export function insertTemplateItem(f: TemplateForm, at: number, item: TemplateItemDraft): TemplateForm {
  const items = [...f.items];
  items.splice(Math.max(0, Math.min(at, items.length)), 0, item);
  return { ...f, items };
}

/** t721u: an item as the server stores it (same rules as the API), to tell whether saving makes a new version. */
function storedItem(it: TemplateItemDraft) {
  return {
    id: it.id ?? null, type: it.type, label: it.label.trim().slice(0, 300), help: it.help?.trim().slice(0, 500) || null, required: Boolean(it.required),
    min: it.type === 'number' ? it.min ?? null : null, max: it.type === 'number' ? it.max ?? null : null, unit: it.type === 'number' ? it.unit?.slice(0, 20) ?? null : null,
    weight: it.weight ?? 1, pass_answer: it.type === 'yes_no_na' ? it.pass_answer ?? 'yes' : null, on_fail: it.on_fail ?? 'none',
  };
}
/** t725u: the headings as sent, placed among the items that are saved (items without a label are dropped). */
export function templateFormSections(f: Pick<TemplateForm, 'items' | 'sections'>): ListSection[] {
  const kept: number[] = [];
  let n = 0;
  for (const it of f.items) { kept.push(n); if (it.label.trim()) n++; }
  kept.push(n);
  return cleanListSections((f.sections ?? []).map(s => ({ ...s, at: kept[Math.max(0, Math.min(s.at, f.items.length))] ?? n })));
}

/** t721u: true when saving the form makes a new version of a saved template (its items, or a register's scope, changed).
 *  Name, description, icon, category, buildings and other settings are template details and keep the version. */
export function templateMakesNewVersion(tpl: ChecklistTemplate | null, f: TemplateForm): boolean {
  if (!tpl) return false;
  if (f.kind === 'register') {
    return !sameJson([f.register_location_id || null, f.register_team_ids, f.register_position_ids, Boolean(f.task_project_id)],
      [tpl.register_location_id ?? null, tpl.register_team_ids ?? [], tpl.register_position_ids ?? [], Boolean(tpl.absent_follow_up)]);
  }
  const items = f.items.filter(i => i.label.trim()).map(storedItem);
  if (!sameJson(items, tpl.items.map(storedItem))) return true;
  // t725u: adding, moving, renaming or removing a section is a change to the items too.
  return f.sections !== undefined && !sameJson(templateFormSections(f), cleanListSections(tpl.sections ?? []));
}

/** Why it can't be saved yet, or null. */
export function validateTemplateForm(f: TemplateForm): string | null {
  if (!f.name.trim()) return 'Give it a name';
  if (f.kind === 'register') return f.register_location_id ? null : 'Choose a building';
  if (!f.items.some(i => i.label.trim())) return 'Add at least one item';
  if (f.is_audit && (!Number.isFinite(f.pass_mark) || f.pass_mark < 0 || f.pass_mark > 100)) return 'Pass mark is 0 to 100';
  return null;
}

/** The body for createChecklistTemplate / updateChecklistTemplate (same as desktop's Save template). */
export function templatePayload(f: TemplateForm) {
  const base = { name: f.name.trim(), description: f.description.trim() || null, category: f.category, kind: f.kind, task_project_id: f.task_project_id || null,
    ...(f.emoji !== undefined ? { emoji: f.emoji.trim() || null } : {}) };
  if (f.kind === 'register') {
    return { ...base, is_audit: false, pass_mark: null, items: [], absent_follow_up: Boolean(f.task_project_id),
      register_location_id: f.register_location_id, register_team_ids: f.register_team_ids, register_position_ids: f.register_position_ids };
  }
  return { ...base, is_audit: f.is_audit, pass_mark: f.is_audit ? f.pass_mark : null, items: f.items.filter(i => i.label.trim()).map(i => ({ ...i, label: i.label.trim() })),
    location_ids: f.location_ids.length ? f.location_ids : null, ...(f.sections !== undefined ? { sections: templateFormSections(f) } : {}) };
}
export const templateSavedToast = (isNew: boolean) => isNew ? 'Template created' : 'Template saved - new runs use this version';

/** t695u: tick or untick a building; ticking every building (or none) means "All buildings" ([]). */
export function toggleTemplateBuilding(f: TemplateForm, id: string, all: readonly string[]): TemplateForm {
  const cur = f.location_ids.length ? f.location_ids : [...all];
  const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
  return { ...f, location_ids: !next.length || all.every(x => next.includes(x)) ? [] : next };
}
/** t695u: "All buildings", "Bangor, Wrexham" or "3 buildings". */
export function templateBuildingsLabel(ids: readonly string[], names: ReadonlyMap<string, string>): string {
  if (!ids.length) return 'All buildings';
  const n = ids.map(id => names.get(id)).filter(Boolean) as string[];
  return n.length && n.length <= 2 ? n.join(', ') : `${ids.length} buildings`;
}
